import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import { authContext } from "../contexts/AuthProvider";
import { mobileMenuContext } from "../contexts/MobileMenuProvider";
import UserMenu from "./UserMenu";
import ThemeMenu from "./ThemeMenu";
import { Link } from "react-router-dom";
import PermissionGate from "./PermissionGate";

const NAV_TABS = [
  { key: "overview", label: "Overview", to: "/dashboard", match: "/dashboard/*" },
  {
    key: "merchants",
    label: "Merchants",
    to: "/merchants/all-merchants",
    match: "/merchants/*",
  },
  {
    key: "drivers",
    label: "Drivers",
    to: "/drivers/all-drivers",
    match: "/drivers/*",
  },
  {
    key: "orders",
    label: "Orders",
    to: "/orders/all-orders",
    match: "/orders/*",
  },
  {
    key: "delivery-companies",
    label: "Delivery Companies",
    to: "/delivary-companies/all-delivary-companies",
    match: "/delivary-companies/*",
  },
  {
    key: "archive",
    label: "Archive",
    to: "/archive/merchants",
    match: "/archive/*",
  },
  {
    key: "settings",
    label: "Settings",
    to: "/settings/configure-roles",
    match: "/settings/*",
  },
  {
    key: "reports",
    label: "Reports",
    to: "/reports/driver-report",
    match: "/reports/*",
  },
  {
    key: "integrations",
    label: "Integrations",
    to: "/integrations/ordable",
    match: "/integrations/*",
    permission: "view_integration",
  },
];

const Header = () => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const { isAuth, role, permissions } = useContext(authContext);
  const { setOpenMenu } = useContext(mobileMenuContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  useEffect(() => {
    if (isAuth === false) {
      navigate("/login");
    }
  }, [isAuth]);

  const hasPermission = (required) => {
    if (!required) return true;
    if (role === "owner") return true;
    return Array.isArray(permissions) && permissions.includes(required);
  };

  const isTabActive = (tab) =>
    !!(pathname && matchPath({ path: tab.match }, pathname) !== null);

  const navTabs = NAV_TABS.filter((tab) => hasPermission(tab.permission));

  const tabsScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = tabsScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return undefined;

    updateScrollState();

    el.addEventListener("scroll", updateScrollState, { passive: true });

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateScrollState);
      observer.observe(el);
    }
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      if (observer) observer.disconnect();
      window.removeEventListener("resize", updateScrollState);
    };
    // eslint-disable-next-line
  }, [navTabs.length]);

  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const activeLink = el.querySelector(".nav-link.active");
    if (activeLink && activeLink.scrollIntoView) {
      activeLink.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "smooth",
      });
    }
    const timer = setTimeout(updateScrollState, 150);
    updateScrollState();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [pathname]);

  const scrollTabs = (direction) => {
    const el = tabsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 220, behavior: "smooth" });
  };

  return (
    <div id="kt_header" className="header">
      {/*begin::Header top*/}
      <div className="header-top d-flex align-items-stretch flex-grow-1">
        {/*begin::Container*/}
        <div className="d-flex  container-xxl  align-items-stretch">
          {/*begin::Brand*/}
          <div
            className="d-flex align-items-center align-items-lg-stretch me-5 flex-row-fluid"
            style={{ minWidth: 0 }}
          >
            {/*begin::Heaeder navs toggle*/}
            <button
              onClick={() => {
                setOpenMenu((prev) => !prev);
              }}
              className="d-lg-none btn btn-icon btn-color-white bg-hover-white bg-hover-opacity-10 w-35px h-35px h-md-40px w-md-40px ms-n3 me-2"
              id="kt_header_navs_toggle"
            >
              <i className="ki-duotone ki-abstract-14 fs-2">
                <span className="path1"></span>
                <span className="path2"></span>
              </i>{" "}
            </button>
            {/*end::Heaeder navs toggle*/}

            {/*begin::Logo*/}
            <Link to="/" className="d-flex align-items-center">
              <img
                alt="Logo"
                src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/logos/logo-light.png`}
                style={{ width: "80px" }}
              />
            </Link>
            {/*end::Logo*/}

            {/*begin::Tabs wrapper*/}
            <div
              className="align-self-end position-relative"
              id="kt_brand_tabs"
              style={{ minWidth: 0, flex: "1 1 auto" }}
            >
              {/*begin::Scroll-hide style — only for this one scroll strip*/}
              <style>{`
                #kt_header_tabs_scroll::-webkit-scrollbar { display: none; }
                #kt_header_tabs_scroll {
                  -ms-overflow-style: none;
                  scrollbar-width: none;
                }
              `}</style>
              {/*end::Scroll-hide style*/}

              {/*begin::Left arrow*/}
              {canScrollLeft && (
                <button
                  type="button"
                  onClick={() => scrollTabs(-1)}
                  aria-label="Scroll tabs left"
                  style={{
                    position: "absolute",
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    border: "none",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    fontSize: "14px",
                    color: "#fff",
                    background: "rgba(255,255,255,0.14)",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                    cursor: "pointer",
                  }}
                >
                  ‹
                </button>
              )}
              {/*end::Left arrow*/}

              {/*begin::Header tabs wrapper*/}
              <div
                ref={tabsScrollRef}
                id="kt_header_tabs_scroll"
                className="header-tabs mx-4 ms-lg-10 mb-5 mb-lg-0"
                style={{
                  minWidth: 0,
                  overflowX: "auto",
                  overflowY: "hidden",
                  paddingLeft: canScrollLeft ? "26px" : 0,
                  paddingRight: canScrollRight ? "26px" : 0,
                  transition: "padding 0.15s ease",
                }}
              >
                {/*begin::Header tabs*/}
                <ul className="nav flex-nowrap text-nowrap align-items-center">
                  {navTabs.map((tab) => (
                    <li className="nav-item" key={tab.key}>
                      <Link
                        className={
                          isTabActive(tab) ? "nav-link active" : "nav-link"
                        }
                        to={tab.to}
                      >
                        {tab.label}
                      </Link>
                    </li>
                  ))}
                </ul>
                {/*end::Header tabs*/}
              </div>
              {/*end::Header tabs wrapper*/}

              {/*begin::Right arrow*/}
              {canScrollRight && (
                <button
                  type="button"
                  onClick={() => scrollTabs(1)}
                  aria-label="Scroll tabs right"
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    border: "none",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    fontSize: "14px",
                    color: "#fff",
                    background: "rgba(255,255,255,0.14)",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                    cursor: "pointer",
                  }}
                >
                  ›
                </button>
              )}
              {/*end::Right arrow*/}
            </div>
            {/*end::Tabs wrapper*/}
          </div>
          {/*end::Brand*/}
          {/*begin::Topbar*/}
          <div className="d-flex align-items-center flex-row-auto">
            {/*begin::Theme mode*/}

            <div
              className="d-flex align-items-center ms-1 p-2 theme-mode-wrapper"
              onMouseEnter={() => {
                setShowThemeMenu(true);
              }}
              onMouseLeave={() => {
                setShowThemeMenu(false);
              }}
              data-kt-menu-attach="parent"
              data-kt-menu-placement="bottom-end"
            >
              {/*begin::Menu toggle*/}
              <div
                style={{ cursor: "pointer" }}
                className="btn btn-icon btn-color-white bg-hover-white bg-hover-opacity-10 w-35px h-35px h-md-40px w-md-40px theme-mode-toggle"
              >
                <i className="ki-duotone ki-night-day theme-light-show fs-2">
                  <span className="path1"></span>
                  <span className="path2"></span>
                  <span className="path3"></span>
                  <span className="path4"></span>
                  <span className="path5"></span>
                  <span className="path6"></span>
                  <span className="path7"></span>
                  <span className="path8"></span>
                  <span className="path9"></span>
                  <span className="path10"></span>
                </i>{" "}
                <i className="ki-duotone ki-moon theme-dark-show fs-2">
                  <span className="path1"></span>
                  <span className="path2"></span>
                </i>
              </div>
              {/*begin::Menu toggle*/}

              {/*begin::Menu*/}
              {showThemeMenu && (
                <ThemeMenu setShowThemeMenu={setShowThemeMenu} />
              )}
              {/*end::Menu*/}
            </div>
            {/*end::Theme mode*/}

            {/*begin::User*/}
            <div
              onMouseEnter={() => {
                setShowUserMenu(true);
              }}
              onMouseLeave={() => {
                setShowUserMenu(false);
              }}
              className="d-flex align-items-center ms-1 user-menu-wrapper"
              id="kt_header_user_menu_toggle"
            >
              {/*begin::User info*/}
              <div
                className="btn btn-flex align-items-center bg-hover-white bg-hover-opacity-10 py-2 ps-2 pe-2 me-n2"
                data-kt-menu-trigger="click"
                data-kt-menu-attach="parent"
                data-kt-menu-placement="bottom-end"
              >
                {/*begin::Name*/}
                <div className="d-none d-md-flex flex-column align-items-end justify-content-center me-2 me-md-4">
                  <span className="text-white opacity-75 fs-8 fw-semibold lh-1 mb-1">
                    {process.env.REACT_APP_BRAND_NAME || ""}
                  </span>
                  <span className="text-white fs-8 fw-bold lh-1">
                    Admin Panel
                  </span>
                </div>
                {/*end::Name*/}

                {/*begin::Symbol*/}
                <div className="symbol symbol-30px symbol-md-40px">
                  <img
                    src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/avatars/user.png`}
                    alt="image"
                  />
                </div>
                {/*end::Symbol*/}
              </div>
              {/*end::User info*/}

              {/*begin::User account menu*/}
              {showUserMenu && <UserMenu />}
              {/*end::User account menu*/}
            </div>
            {/*end::User */}
          </div>
          {/*end::Topbar*/}{" "}
        </div>
        {/*end::Container*/}
      </div>
      {/*end::Header top*/}

      {/*begin::Header navs*/}
      <div
        className="header-navs d-flex align-items-stretch flex-stack h-lg-70px w-100 py-5 py-lg-0 overflow-hidden overflow-lg-visible"
        id="kt_header_navs"
        data-kt-drawer="true"
        data-kt-drawer-name="header-menu"
        data-kt-drawer-activate="{default: true, lg: false}"
        data-kt-drawer-overlay="true"
        data-kt-drawer-width="{default:'200px', '300px': '250px'}"
        data-kt-drawer-direction="start"
        data-kt-drawer-toggle="#kt_header_navs_toggle"
        data-kt-swapper="true"
        data-kt-swapper-mode="append"
        data-kt-swapper-parent="{default: '#kt_body', lg: '#kt_header'}"
      >
        {/*begin::Container*/}
        <div className="d-lg-flex  container-xxl  w-100">
          {/*begin::Wrapper*/}
          <div
            className="d-lg-flex flex-column justify-content-lg-center w-100"
            id="kt_header_navs_wrapper"
          >
            {/*begin::Header tab content*/}
            <div
              className="tab-content"
              data-kt-scroll="true"
              data-kt-scroll-activate="{default: true, lg: false}"
              data-kt-scroll-height="auto"
              data-kt-scroll-offset="70px"
            >
              {/*begin::Tab panel*/}
              {pathname &&
                matchPath({ path: "/dashboard/*" }, pathname) !== null ? (
                <div
                  className="tab-pane fade active show"
                  id="kt_header_navs_tab_1"
                >
                  {/*begin::Wrapper*/}
                  <div className="d-flex flex-column flex-lg-row flex-lg-stack flex-wrap gap-2 px-4 px-lg-0">
                    <div className="d-flex flex-column flex-lg-row gap-2">
                      <Link
                        className={
                          pathname && pathname === "/dashboard"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/dashboard"
                      >
                        Driver Map
                      </Link>
                      <Link
                        className={
                          pathname && pathname === "/dashboard/ongoing-orders"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/dashboard/ongoing-orders"
                      >
                        Ongoing Orders
                      </Link>
                    </div>
                  </div>
                  {/*end::Wrapper*/}
                </div>
              ) : (
                ""
              )}
              {pathname &&
                matchPath({ path: "/merchants/*" }, pathname) !== null ? (
                <div
                  className="tab-pane fade active show"
                  id="kt_header_navs_tab_2"
                >
                  {/*begin::Wrapper*/}
                  <div className="d-flex flex-column flex-lg-row flex-lg-stack flex-wrap gap-2 px-4 px-lg-0">
                    <div className="d-flex flex-column flex-lg-row gap-2">
                      <Link
                        className={
                          pathname && pathname === "/merchants/all-merchants"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/merchants/all-merchants"
                      >
                        All Merchants
                      </Link>
                      <Link
                        className={
                          pathname && pathname === "/merchants/create-merchant"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/merchants/create-merchant"
                      >
                        Create New
                      </Link>
                      <PermissionGate
                        required={`view_merchant_signups`}
                        fallback={<></>}
                        show={false}
                      >
                        <Link
                          className={
                            pathname &&
                              matchPath(
                                { path: "/merchants/signup-details/:id" },
                                pathname,
                              ) !== null
                              ? "btn btn-sm btn-primary fw-bold"
                              : pathname && pathname === "/merchants/signups"
                                ? "btn btn-sm btn-primary fw-bold"
                                : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/merchants/signups"
                        >
                          Signups
                        </Link>
                      </PermissionGate>
                      <Link
                        className={
                          pathname && pathname === "/merchants/groups"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/merchants/groups"
                      >
                        Groups
                      </Link>
                      <Link
                        className={
                          pathname && pathname === "/merchants/pricing-sheet"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/merchants/pricing-sheet"
                      >
                        Pricing Sheet
                      </Link>
                      <Link
                        className={
                          pathname && pathname === "/merchants/all-transactions"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/merchants/all-transactions"
                      >
                        All Transactions
                      </Link>
                      <PermissionGate
                        required={`view_all_merchants_consumption_list`}
                        fallback={<></>}
                        show={false}
                      >
                        <Link
                          className={
                            pathname && pathname === "/merchants/consumption"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/merchants/consumption"
                        >
                          Consumption
                        </Link>
                      </PermissionGate>
                      <Link
                        className={
                          pathname &&
                            pathname === "/merchants/all-notifications"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/merchants/all-notifications"
                      >
                        Notifications
                      </Link>
                      <PermissionGate
                        required={`view_low_balance_alert_setting`}
                        fallback={<></>}
                        show={false}
                      >
                        <Link
                          className={
                            pathname &&
                              pathname === "/merchants/low-balance-alert"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/merchants/low-balance-alert"
                        >
                          Low Balance Alert
                        </Link>
                      </PermissionGate>
                    </div>
                  </div>
                  {/*end::Wrapper*/}
                </div>
              ) : (
                ""
              )}
              {pathname &&
                matchPath({ path: "/drivers/*" }, pathname) !== null ? (
                <div
                  className="tab-pane fade active show"
                  id="kt_header_navs_tab_2"
                >
                  {/*begin::Wrapper*/}
                  <div className="d-flex flex-column flex-lg-row flex-lg-stack flex-wrap gap-2 px-4 px-lg-0">
                    <div className="d-flex flex-column flex-lg-row gap-2">
                      <Link
                        className={
                          pathname && pathname === "/drivers/all-drivers"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/drivers/all-drivers"
                      >
                        All Drivers
                      </Link>
                      <Link
                        className={
                          pathname && pathname === "/drivers/create-driver"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/drivers/create-driver"
                      >
                        Create New
                      </Link>
                    </div>
                  </div>
                  {/*end::Wrapper*/}
                </div>
              ) : (
                ""
              )}
              {pathname &&
                matchPath({ path: "/orders/*" }, pathname) !== null ? (
                <div className="tab-pane fade active show">
                  {/*begin::Wrapper*/}
                  <div className="d-flex flex-column flex-lg-row flex-lg-stack flex-wrap gap-2 px-4 px-lg-0">
                    <div className="d-flex flex-column flex-lg-row gap-2">
                      <Link
                        className={
                          pathname && pathname === "/orders/all-orders"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/orders/all-orders"
                      >
                        All Orders
                      </Link>
                      <PermissionGate
                        required={`import_manual_orders`}
                        fallback={<></>}
                      >
                        <Link
                          className={
                            pathname &&
                              pathname === "/orders/import-manual-orders"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/orders/import-manual-orders"
                        >
                          Import Orders
                        </Link>
                      </PermissionGate>
                      <PermissionGate
                        required={`import_manual_orders`}
                        fallback={<></>}
                      >
                        <Link
                          className={
                            pathname &&
                              pathname === "/orders/manual-order-entry"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/orders/manual-order-entry"
                        >
                          Enter Orders
                        </Link>
                      </PermissionGate>
                    </div>
                  </div>
                  {/*end::Wrapper*/}
                </div>
              ) : (
                ""
              )}
              {pathname &&
                matchPath({ path: "/delivary-companies/*" }, pathname) !==
                null ? (
                <div
                  className="tab-pane fade active show"
                  id="kt_header_navs_tab_2"
                >
                  {/*begin::Wrapper*/}
                  <div className="d-flex flex-column flex-lg-row flex-lg-stack flex-wrap gap-2 px-4 px-lg-0">
                    <div className="d-flex flex-column flex-lg-row gap-2">
                      <Link
                        className={
                          pathname &&
                            pathname ===
                            "/delivary-companies/all-delivary-companies"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/delivary-companies/all-delivary-companies"
                      >
                        All Companies
                      </Link>
                      <Link
                        className={
                          pathname &&
                            pathname ===
                            "/delivary-companies/create-delivary-company"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/delivary-companies/create-delivary-company"
                      >
                        Create New
                      </Link>
                    </div>
                  </div>
                  {/*end::Wrapper*/}
                </div>
              ) : (
                ""
              )}
              {pathname &&
                matchPath({ path: "/archive/*" }, pathname) !== null ? (
                <div
                  className="tab-pane fade active show"
                  id="kt_header_navs_tab_2"
                >
                  {/*begin::Wrapper*/}
                  <div className="d-flex flex-column flex-lg-row flex-lg-stack flex-wrap gap-2 px-4 px-lg-0">
                    <div className="d-flex flex-column flex-lg-row gap-2">
                      <Link
                        className={
                          pathname && pathname === "/archive/merchants"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/archive/merchants"
                      >
                        Merchants
                      </Link>
                      <Link
                        className={
                          pathname && pathname === "/archive/drivers"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/archive/drivers"
                      >
                        Drivers
                      </Link>
                      <Link
                        className={
                          pathname && pathname === "/archive/employees"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/archive/employees"
                      >
                        Employees
                      </Link>
                      <Link
                        className={
                          pathname && pathname === "/archive/delivary-companies"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/archive/delivary-companies"
                      >
                        Delivary Companies
                      </Link>
                    </div>
                  </div>
                  {/*end::Wrapper*/}
                </div>
              ) : (
                ""
              )}
              {pathname &&
                matchPath({ path: "/settings/*" }, pathname) !== null ? (
                <div
                  className="tab-pane fade active show"
                  id="kt_header_navs_tab_2"
                >
                  {/*begin::Wrapper*/}
                  <div className="d-flex flex-column flex-lg-row flex-lg-stack flex-wrap gap-2 px-4 px-lg-0">
                    <div className="d-flex flex-column flex-lg-row gap-2">
                      <Link
                        className={
                          pathname && pathname === "/settings/configure-roles"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/settings/configure-roles"
                      >
                        Configure Roles
                      </Link>
                      <Link
                        className={
                          pathname && pathname === "/settings/user-role"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/settings/user-role"
                      >
                        User Role
                      </Link>
                      <PermissionGate
                        required={`view_driver_pricing_model_list`}
                        fallback={<></>}
                        show={false}
                      >
                        <Link
                          className={
                            pathname && pathname === "/settings/driver-sharing"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/settings/driver-sharing"
                        >
                          Driver Payout
                        </Link>
                      </PermissionGate>
                      <PermissionGate
                        required={`view_driver_pricing_model_list`}
                        fallback={<></>}
                        show={false}
                      >
                        <Link
                          className={
                            pathname && pathname === "/settings/order-scan-limit"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/settings/order-scan-limit"
                        >
                          Order Scan Limit
                        </Link>
                      </PermissionGate>
                      <Link
                        className={
                          pathname && pathname === "/settings/pricing-model"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/settings/pricing-model"
                      >
                        Pricing Model
                      </Link>
                      <PermissionGate
                        required={`view_driver_pricing_model_list`}
                        fallback={<></>}
                        show={false}
                      >
                        <Link
                          className={
                            pathname &&
                              pathname === "/settings/driver-pricing-model"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/settings/driver-pricing-model"
                        >
                          Driver Pricing Model
                        </Link>
                      </PermissionGate>
                      <PermissionGate
                        required={`view_company_pricing_model_list`}
                        fallback={<></>}
                        show={false}
                      >
                        <Link
                          className={
                            pathname && pathname === "/settings/company-pricing-model"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/settings/company-pricing-model"
                        >
                          Company Commission Model
                        </Link>
                      </PermissionGate>
                      <Link
                        className={
                          pathname && pathname === "/settings/revenue-stat"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/settings/revenue-stat"
                      >
                        Revenue Stat
                      </Link>
                    </div>
                  </div>
                  {/*end::Wrapper*/}
                </div>
              ) : (
                ""
              )}
              {pathname &&
                matchPath({ path: "/reports/*" }, pathname) !== null ? (
                <div
                  className="tab-pane fade active show"
                  id="kt_header_navs_tab_3"
                >
                  {/*begin::Wrapper*/}
                  <div className="d-flex flex-column flex-lg-row flex-lg-stack flex-wrap gap-2 px-4 px-lg-0">
                    <div className="d-flex flex-column flex-lg-row gap-2">
                      <Link
                        className={
                          pathname && pathname === "/reports/driver-report"
                            ? "btn btn-sm btn-primary fw-bold"
                            : "btn btn-sm btn-light-primary fw-bold"
                        }
                        to="/reports/driver-report"
                      >
                        Driver Report
                      </Link>
                      <PermissionGate
                        required={`view_manual_action_report`}
                        fallback={<></>}
                        show={false}
                      >
                        <Link
                          className={
                            pathname &&
                              pathname === "/reports/manual-action-report"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/reports/manual-action-report"
                        >
                          Manual Action Report
                        </Link>
                      </PermissionGate>
                      <PermissionGate
                        required={`view_driver_busy_report`}
                        fallback={<></>}
                        show={false}
                      >
                        <Link
                          className={
                            pathname &&
                              pathname === "/reports/driver-busy-report"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/reports/driver-busy-report"
                        >
                          Driver Busy Report
                        </Link>
                      </PermissionGate>
                    </div>
                  </div>
                  {/*end::Wrapper*/}
                </div>
              ) : (
                ""
              )}
              <PermissionGate
                required={`view_integration`}
                fallback={<></>}
                show={false}
              >
                {pathname &&
                  matchPath({ path: "/integrations/*" }, pathname) !== null ? (
                  <div
                    className="tab-pane fade active show"
                    id="kt_header_navs_tab_2"
                  >
                    {/*begin::Wrapper*/}
                    <div className="d-flex flex-column flex-lg-row flex-lg-stack flex-wrap gap-2 px-4 px-lg-0">
                      <div className="d-flex flex-column flex-lg-row gap-2">
                        <Link
                          className={
                            pathname && pathname === "/integrations/ordable"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/integrations/ordable"
                        >
                          Ordable
                        </Link>
                        <Link
                          className={
                            pathname &&
                              pathname === "/integrations/payment-gateway"
                              ? "btn btn-sm btn-primary fw-bold"
                              : "btn btn-sm btn-light-primary fw-bold"
                          }
                          to="/integrations/payment-gateway"
                        >
                          Payment Gateway
                        </Link>
                      </div>
                    </div>
                    {/*end::Wrapper*/}
                  </div>
                ) : (
                  ""
                )}
              </PermissionGate>

              {/*end::Tab panel*/}

              {/*begin::Tab panel*/}

              {/*end::Tab panel*/}
            </div>
            {/*end::Header tab content*/}
          </div>
          {/*end::Wrapper*/}
        </div>
        {/*end::Container*/}
      </div>
      {/*end::Header navs*/}
    </div>
  );
};

export default Header;